import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react';
import {
  TextArea,
  TextAreaProps,
} from '@lifeomic/chroma-react/components/TextArea';

const DEFAULT_MAX_ROWS = 20;
const FALLBACK_LINE_HEIGHT_PX = 17.5;
const RESIZE_HANDLE_SIZE_PX = 20;

type Props = TextAreaProps & {
  maxRows?: number;
};

/**
 * Chroma's TextArea has a fixed maximum height. Survey notes can contain
 * multi-line chart fills, so size the native textarea to its content while
 * retaining a vertical resize handle for staff.
 */
const AutoGrowTextArea = forwardRef<HTMLTextAreaElement, Props>(
  (
    { maxRows = DEFAULT_MAX_ROWS, onMouseDown, value, ...textAreaProps },
    forwardedRef,
  ) => {
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const hasUserResized = useRef(false);

    useImperativeHandle(forwardedRef, () => textAreaRef.current!, []);

    const resizeToContent = useCallback(() => {
      const textArea = textAreaRef.current;
      if (!textArea || hasUserResized.current) return;

      const computedStyle = window.getComputedStyle(textArea);
      const lineHeight =
        Number.parseFloat(computedStyle.lineHeight) || FALLBACK_LINE_HEIGHT_PX;
      const verticalPadding =
        (Number.parseFloat(computedStyle.paddingTop) || 0) +
        (Number.parseFloat(computedStyle.paddingBottom) || 0);
      const maxHeight = lineHeight * maxRows + verticalPadding;

      // Reset first so the control can shrink when content is removed.
      textArea.style.height = 'auto';
      const contentHeight = textArea.scrollHeight;

      // Collapsed sections stay mounted under `display: none`, where every
      // measurement is 0. Writing a height here would pin the control to its
      // min-height with overflow hidden once the section opens.
      if (contentHeight === 0) {
        textArea.style.height = '';
        textArea.style.overflowY = '';
        return;
      }

      textArea.style.height = `${Math.min(contentHeight, maxHeight)}px`;
      textArea.style.overflowY = contentHeight > maxHeight ? 'auto' : 'hidden';
    }, [maxRows]);

    useLayoutEffect(() => {
      resizeToContent();
    }, [resizeToContent, value]);

    useLayoutEffect(() => {
      window.addEventListener('resize', resizeToContent);
      return () => window.removeEventListener('resize', resizeToContent);
    }, [resizeToContent]);

    // A field inside a collapsed section is unmeasurable until the section
    // opens, which is not a render of this component. Observing the control
    // catches that transition, plus any layout change that rewraps the text.
    useLayoutEffect(() => {
      const textArea = textAreaRef.current;
      if (!textArea || typeof ResizeObserver === 'undefined') return;

      const observer = new ResizeObserver(() => resizeToContent());
      observer.observe(textArea);
      return () => observer.disconnect();
    }, [resizeToContent]);

    const handleMouseDown = (event: React.MouseEvent<HTMLTextAreaElement>) => {
      const textArea = textAreaRef.current;
      if (textArea) {
        const bounds = textArea.getBoundingClientRect();
        const isResizeHandle =
          event.clientX >= bounds.right - RESIZE_HANDLE_SIZE_PX &&
          event.clientY >= bounds.bottom - RESIZE_HANDLE_SIZE_PX;
        if (isResizeHandle) {
          hasUserResized.current = true;
          // The user now owns the height. Keep overflow available if they
          // drag shorter than the content or add more text afterward.
          textArea.style.overflowY = 'auto';
        }
      }
      onMouseDown?.(event);
    };

    return (
      <TextArea
        {...textAreaProps}
        value={value}
        ref={textAreaRef}
        onMouseDown={handleMouseDown}
      />
    );
  },
);

AutoGrowTextArea.displayName = 'AutoGrowTextArea';

export default AutoGrowTextArea;
